import React, { useMemo, useState } from 'react';
import { Spinner, Typography } from '@material-tailwind/react';
import { LayoutTemplateIcon } from 'lucide-react';
import { canEditFloorPlan } from './constants';
import { useAuth } from '../../services/contexts/authContext';

function occupancyColor(rate, status) {
  if (status && status !== 'operational') {
    if (status === 'in_maintenance') return { fill: '#9ca3af', accent: '#6b7280' };
    if (status === 'under_renovation') return { fill: '#fbbf24', accent: '#d97706' };
    if (status === 'closed') return { fill: '#f87171', accent: '#b91c1c' };
  }
  if (rate == null || Number.isNaN(rate)) return { fill: '#d1d5db', accent: '#9ca3af' };
  if (rate >= 80) return { fill: '#f87171', accent: '#dc2626' };
  if (rate >= 50) return { fill: '#fbbf24', accent: '#d97706' };
  if (rate > 0) return { fill: '#4ade80', accent: '#16a34a' };
  return { fill: '#86efac', accent: '#22c55e' };
}

/**
 * Isometric stacked-slab building visualization (SVG/CSS, not real 3D).
 * Mobile falls back to a vertical card list.
 */
const BuildingStack = ({
  floors = [],
  building,
  isLoading,
  onSelectFloor,
  onCreateLayout,
  selectedFloorId = null,
}) => {
  const { userDetails } = useAuth();
  const canEdit = canEditFloorPlan(userDetails?.role);
  const [hoveredId, setHoveredId] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Bottom → top stacking: lowest floor number at bottom
  const sorted = useMemo(
    () => [...floors].sort((a, b) => (a.number || 0) - (b.number || 0)),
    [floors]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!sorted.length) {
    return (
      <div className="rounded-2xl border border-dashed p-10 text-center text-gray-500">
        No floors in this building yet.
      </div>
    );
  }

  const slabW = 280;
  const slabH = 36;
  const depth = 28;
  const gap = 18;
  const svgW = 420;
  const svgH = sorted.length * (slabH + gap) + depth + 80;

  return (
    <div className="space-y-4">
      <div>
        <Typography variant="h5">{building?.name || 'Building'}</Typography>
        <Typography variant="small" className="text-gray-500">
          {building?.buildingCode} · Select a floor to open its plan
        </Typography>
      </div>

      {/* Desktop isometric stack */}
      <div className="hidden md:flex justify-center relative">
        <svg
          width={svgW}
          height={svgH}
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="overflow-visible"
        >
          {sorted.map((floor, index) => {
            const fromBottom = index;
            const baseY = svgH - 50 - fromBottom * (slabH + gap);
            const cx = svgW / 2;
            const lifted = hoveredId === floor._id || selectedFloorId === floor._id;
            const lift = lifted ? -12 : 0;
            const colors = occupancyColor(floor.occupancyRate, floor.status);
            const greyed = !floor.hasLayout;
            const fill = greyed ? '#e5e7eb' : colors.fill;
            const accent = greyed ? '#9ca3af' : colors.accent;

            // Isometric parallelogram points
            const x0 = cx - slabW / 2;
            const y0 = baseY + lift;
            const top = [
              [x0, y0],
              [x0 + slabW, y0],
              [x0 + slabW + depth, y0 - depth],
              [x0 + depth, y0 - depth],
            ];
            const front = [
              [x0, y0],
              [x0 + slabW, y0],
              [x0 + slabW, y0 + slabH],
              [x0, y0 + slabH],
            ];
            const side = [
              [x0 + slabW, y0],
              [x0 + slabW + depth, y0 - depth],
              [x0 + slabW + depth, y0 - depth + slabH],
              [x0 + slabW, y0 + slabH],
            ];

            const poly = (pts) => pts.map((p) => p.join(',')).join(' ');

            return (
              <g
                key={floor._id}
                className="cursor-pointer"
                style={{ transition: 'transform 200ms ease' }}
                onMouseEnter={(e) => {
                  setHoveredId(floor._id);
                  setTooltip({
                    floor,
                    x: e.clientX,
                    y: e.clientY,
                  });
                }}
                onMouseLeave={() => {
                  setHoveredId(null);
                  setTooltip(null);
                }}
                onClick={() => onSelectFloor?.(floor)}
              >
                <polygon points={poly(top)} fill={fill} stroke={accent} strokeWidth={lifted ? 2.5 : 1.5} opacity={greyed ? 0.7 : 1} />
                <polygon points={poly(front)} fill={accent} opacity={0.85} />
                <polygon points={poly(side)} fill={accent} opacity={0.55} />
                <text
                  x={cx}
                  y={y0 + slabH / 2 + 4}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="#111827"
                >
                  {floor.floorCode || `F${floor.number}`}
                  {' · '}
                  {floor.occupiedSeats || 0}/{floor.totalSeats || 0}
                </text>
                {greyed && (
                  <text
                    x={cx}
                    y={y0 - depth / 2 - 4}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    {canEdit ? 'Create layout' : 'No layout'}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {tooltip && (
          <div
            className="pointer-events-none fixed z-50 rounded-xl bg-white border shadow-lg px-3 py-2 text-xs"
            style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
          >
            <div className="font-semibold">{tooltip.floor.floorCode}</div>
            <div>Occupied: {tooltip.floor.occupiedSeats}/{tooltip.floor.totalSeats}</div>
            <div>Rate: {(tooltip.floor.occupancyRate || 0).toFixed(1)}%</div>
            <div className="capitalize">Status: {tooltip.floor.status}</div>
            {!tooltip.floor.hasLayout && <div className="text-orange-700 mt-1">No published layout</div>}
          </div>
        )}
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2">
        {[...sorted].reverse().map((floor) => {
          const colors = occupancyColor(floor.occupancyRate, floor.status);
          const greyed = !floor.hasLayout;
          return (
            <button
              key={floor._id}
              type="button"
              onClick={() => onSelectFloor?.(floor)}
              className={`w-full text-left rounded-2xl border p-4 flex items-center justify-between gap-3 hover:bg-gray-50 ${selectedFloorId === floor._id ? 'ring-2 ring-orange-400 bg-orange-50/40' : ''}`}
              style={{ borderLeftWidth: 6, borderLeftColor: greyed ? '#9ca3af' : colors.accent }}
            >
              <div>
                <div className="font-semibold">{floor.floorCode || `Floor ${floor.number}`}</div>
                <div className="text-xs text-gray-500 capitalize">{floor.status}</div>
                {!floor.hasLayout && (
                  <div className="text-xs text-orange-700 mt-1 flex items-center gap-1">
                    <LayoutTemplateIcon size={12} />
                    {canEdit ? 'Create layout' : 'No layout yet'}
                  </div>
                )}
              </div>
              <div className="text-right text-sm">
                <div className="font-medium">{floor.occupiedSeats || 0}/{floor.totalSeats || 0}</div>
                <div className="text-xs text-gray-500">{(floor.occupancyRate || 0).toFixed(0)}%</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-400" /> High occupancy</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-yellow-400" /> Mid</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-green-400" /> Low</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gray-300" /> No layout</span>
      </div>
    </div>
  );
};

export default BuildingStack;
